import { ReactNode, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { SCHEDULE_MARK_BILLED_LABEL } from "./scheduleBillingCopy";

interface ScheduleBillingModalShellProps<Target> {
    target: Target | null;
    onHide: () => void;
    children: (target: Target) => ReactNode;
}

/** Mark as billed modal frame; keeps the form mounted through the exit animation. */
export const ScheduleBillingModalShell = <Target,>({
    target,
    onHide,
    children,
}: ScheduleBillingModalShellProps<Target>) => {
    const [displayedTarget, setDisplayedTarget] = useState(target);
    if (target && target !== displayedTarget) {
        setDisplayedTarget(target);
    }

    return (
        <PlainModal
            show={target !== null}
            onHide={onHide}
            onExited={() => setDisplayedTarget(null)}
            size="md"
        >
            <StandardModalHeader>
                {SCHEDULE_MARK_BILLED_LABEL}
            </StandardModalHeader>
            {displayedTarget && children(displayedTarget)}
        </PlainModal>
    );
};

export const ScheduleBillingModalFooter = ({
    isSubmitting,
    isDisabled = false,
    onCancel,
}: {
    isSubmitting: boolean;
    isDisabled?: boolean;
    onCancel: () => void;
}) => (
    <HStack as="footer" gap={8} justify="end">
        <Button type="button" variant="secondary" size="xl" onClick={onCancel}>
            Cancel
        </Button>
        <ButtonWithLoader
            type="submit"
            variant="primary"
            size="xl"
            loading={isSubmitting}
            disabled={isDisabled}
        >
            {SCHEDULE_MARK_BILLED_LABEL}
        </ButtonWithLoader>
    </HStack>
);
